import * as THREE from 'three';

/**
 * THE BODY'S OWN RIG CORRECTIONS — the numbers tuned in the website's Model Lab,
 * applied to a resident standing in the World.
 *
 * Khari, 2026-08-31: *"their idle settings (shoulder, elbows, etc.) made in the
 * website editor should also reflect here."*
 *
 * `shoulderDrop`, `armOut`, `elbowIn`, `headTilt` and `armLiftMax` are stored on
 * `pose` against the body (`world_assets.variants[].pose`) — they are properties
 * of THIS RIG, not of any one animation, which is why they belong on every
 * surface that draws it. Until now only the web viewer applied them.
 *
 * ⚠ ADAPTED FROM, NOT SHARED WITH, `Web/src/components/ModelViewer3D.jsx`
 * (`applyShoulders`). Unlike `faceDecal.js` — which is a guarded verbatim copy —
 * the original is 230 lines tangled into that component's raf loop, refs and
 * slider readout, and pulling it out means surgery on the viewer that is
 * currently driving the channel and the Lab. So this is a second implementation
 * of the same algorithm, and these invariants MUST stay in step with it:
 *
 *   1. IDEMPOTENCE (see `beginCorrection`) — the single most expensive bug this
 *      code has produced. Non-negotiable.
 *   2. SHOULDER BEFORE ARM — the upper arm is a child of the clavicle.
 *   3. THE LIFT CEILING RUNS AFTER `armOut`, in its OWN loop.
 *   4. `headTilt`'s axis comes off the SHOULDERS, and POSITIVE IS UP.
 *
 * ⚠ THE CEILING'S AXIS IS `dir × down`, NOT A FIXED CORONAL AXIS — an arm
 * raised forward has to come down forward. Measured on Big_Wave_Hello, which
 * lifts to 170°: a fixed axis swings it out to the side on the way down, which
 * is a different gesture.
 */

const DEG = Math.PI / 180;

/**
 * @param {THREE.Object3D} root a cloned, skeleton-safe body
 * @returns {{usable: boolean, apply: (v: object) => object|null}}
 */
export function createPoseFix(root) {
  /* ⚠ BONES ARE LOOKED UP ONCE, unlike the web viewer which calls
     `getObjectByName` eight times a frame. That is free when one body fills the
     screen and is not when twelve share a room: `getObjectByName` is a full
     traverse, so this was 96 traversals a frame. The skeleton of a cloned body
     never changes, so caching is safe as well as cheap. */
  const byName = (n) => root.getObjectByName(n) || null;
  /* `neck` is lowercase on this rig while `Head` is not — the rigger's
     business, not ours. Suffix-matched, case-insensitively, which also keeps
     `head_end` and `headfront` out (they end in `_end` and `front`). */
  const findSuffix = (suffix) => {
    let hit = null;
    root.traverse((o) => {
      if (hit || !o.isBone) return;
      if ((o.name || '').toLowerCase().endsWith(suffix)) hit = o;
    });
    return hit;
  };
  const B = {
    LeftShoulder: byName('LeftShoulder'),
    RightShoulder: byName('RightShoulder'),
    LeftArm: byName('LeftArm'),
    RightArm: byName('RightArm'),
    LeftForeArm: byName('LeftForeArm'),
    RightForeArm: byName('RightForeArm'),
    neck: findSuffix('neck'),
    head: findSuffix('head'),
  };
  /* A rig with no upper arms is not one these numbers describe. Reporting it
     rather than half-applying: a correction that silently lands on two of six
     bones is a deformed body with no error to search for. */
  const usable = !!(B.LeftArm && B.RightArm);

  // Scratch — a Vector3 per bone per frame per resident would churn the GC in a
  // populated room. Each consumer gets its OWN pair: `armA/armB` are consumed by
  // armOpenness inside the same frame as the ceiling's measurement, and sharing
  // them made the result depend on call order.
  const shoulderAxis = new THREE.Vector3(0, 0, 1);
  const armA = new THREE.Vector3();
  const armB = new THREE.Vector3();
  const headAxis = new THREE.Vector3();
  const headA = new THREE.Vector3();
  const headB = new THREE.Vector3();
  const clampA = new THREE.Vector3();
  const clampB = new THREE.Vector3();
  const clampAxis = new THREE.Vector3();
  const DOWN = new THREE.Vector3(0, -1, 0);

  /** How far this arm is from hanging straight down, in degrees. */
  const armLiftDeg = (bone) => {
    const child = bone.children.find((c) => c.isBone);
    if (!child) return 0;
    bone.getWorldPosition(armA);
    child.getWorldPosition(armB);
    const down = -armB.sub(armA).normalize().y;
    return Math.acos(Math.max(-1, Math.min(1, down))) / DEG;
  };

  /* ⚠ `armOut` FADES OUT AS THE POSE OPENS THE ARM ITSELF. A raised arm already
     has daylight under it; a flat correction would swing those wider and break
     the poses that were never the problem. Full strength on an arm hanging dead
     straight, nothing left by `armOutFade` degrees of lift. */
  const armOpenness = (bone, fade) => {
    const child = bone.children.find((c) => c.isBone);
    if (!child) return 1;
    const deg = armLiftDeg(bone);
    const to = fade > 1 ? fade : 45;
    const from = Math.max(0, to - 28);
    return 1 - Math.max(0, Math.min(1, (deg - from) / Math.max(1, to - from)));
  };

  /* ── THE CORRECTIONS ARE ADDITIVE, SO THEY MUST LAND EXACTLY ONCE A FRAME ──
     Every correction below is a `rotateOnWorldAxis`, which ADDS to whatever the
     bone currently holds. That is only safe because something OVERWRITES the
     bone first each frame — the mixer, or the pose loop — so the offset lands on
     a clean value. **Neither always writes.** A bone the current clip has no
     track for is left exactly as it was, which is last frame's ALREADY-CORRECTED
     value, and the offset goes on again: measured at +25°/frame in the web
     viewer, which is how a resident ends up looking at the ceiling with its
     elbows inside out.

     ⚠ IN THE WORLD THERE ARE THREE WRITERS, NOT ONE. The mixer (walking/idle),
     the pose loop (a seated barber, absolute every frame), and the talking
     head-look — which writes the head only while amplitude is up and then STOPS
     DEAD mid-conversation. That last one is the exact silent-writer case, on the
     exact bone `headTilt` corrects, every time somebody stops talking.

     ⚠ THE FIX IS IDEMPOTENCE, NOT ORDERING. Remember what we wrote last frame:
     if the bone still holds it, nothing else wrote, so undo ourselves before
     re-applying; if it holds anything else, that is a fresh value and becomes
     the new base. Correct whether the writer runs every frame, some frames, or
     never again. */
  const corrections = new Map();
  const correctedBones = [];
  const beginCorrection = (bone) => {
    if (!bone) return;
    correctedBones.push(bone);
    let rec = corrections.get(bone);
    if (!rec) {
      rec = { base: bone.quaternion.clone(), out: bone.quaternion.clone() };
      corrections.set(bone, rec);
      return;
    }
    if (bone.quaternion.equals(rec.out)) bone.quaternion.copy(rec.base);
    else rec.base.copy(bone.quaternion);
  };

  /**
   * Run one frame of corrections. Call AFTER every other writer.
   * @returns {{lift: number, openness: number, applied: number, clampedBy: number}|null}
   */
  const apply = (v) => {
    if (!usable || !v) return null;
    const drop = Number(v.shoulderDrop) || 0;
    const out = Number(v.armOut) || 0;
    const elbow = Number(v.elbowIn) || 0;
    const tilt = Number(v.headTilt) || 0;
    const liftMax = Number(v.armLiftMax) || 0;
    const fade = Number(v.armOutFade) || 0;

    /* ⚠ EVERY BONE THIS MAY TOUCH, BEFORE ANY OF IT RUNS — including the ones
       whose correction is currently zero, or a value dropped to zero keeps
       compounding on a frame nothing else writes. */
    correctedBones.length = 0;
    for (const side of ['Left', 'Right']) {
      beginCorrection(B[`${side}Shoulder`]);
      beginCorrection(B[`${side}Arm`]);
      beginCorrection(B[`${side}ForeArm`]);
    }
    beginCorrection(B.neck);
    beginCorrection(B.head);

    /* ⚠ SHOULDER FIRST, THEN ARM. The upper arm is a CHILD of the clavicle, so
       turning the clavicle afterwards would carry the arm correction round with
       it and change what it means. */
    if (drop) {
      const rad = drop * DEG;
      for (const side of ['Left', 'Right']) {
        const bone = B[`${side}Shoulder`];
        if (bone) bone.rotateOnWorldAxis(shoulderAxis, side === 'Left' ? rad : -rad);
      }
    }

    if (tilt) {
      /* ⚠ THE LATERAL AXIS COMES OFF THE SHOULDERS, NOT OFF `rotation.y`.
         Deriving it from the model node's own yaw is only right if nothing else
         in the hierarchy turns the body — and in the World the resident group
         is turned to face an approaching player every frame. Shoulder to
         shoulder IS the lateral axis, in world, whatever anything above it has
         done. ⚠ POSITIVE IS UP, which is what the setting is called. */
      const sl = B.LeftShoulder;
      const sr = B.RightShoulder;
      if (sl && sr) {
        sl.getWorldPosition(headA);
        sr.getWorldPosition(headB);
        headAxis.subVectors(headB, headA).normalize();
      } else {
        headAxis.set(Math.cos(root.rotation.y), 0, -Math.sin(root.rotation.y));
      }
      for (const [key, share] of [['neck', 0.4], ['head', 0.6]]) {
        const bone = B[key];
        if (bone) bone.rotateOnWorldAxis(headAxis, tilt * share * DEG);
      }
    }

    let reported = null;
    for (const side of ['Left', 'Right']) {
      const bone = B[`${side}Arm`];
      if (!bone) continue;
      const open = armOpenness(bone, fade);
      if (side === 'Left') reported = { lift: armLiftDeg(bone), openness: open };
      if (!out) continue;
      const rad = out * open * DEG;
      if (rad) bone.rotateOnWorldAxis(shoulderAxis, side === 'Left' ? rad : -rad);
    }

    /* ── THE LIFT CEILING ──────────────────────────────────────────────────
       AFTER `armOut`, because armOut ADDS lift and a ceiling measured before it
       would be a ceiling on the wrong number. Its own loop rather than the one
       above: that one does `if (!out) continue`, so everything after it would be
       dead whenever `armOut` is 0 — which is exactly the case a big wave needs
       the ceiling for. */
    let clampedBy = 0;
    if (liftMax > 0) {
      for (const side of ['Left', 'Right']) {
        const bone = B[`${side}Arm`];
        if (!bone) continue;
        const over = armLiftDeg(bone) - liftMax;
        if (over <= 0.01) continue;
        const child = bone.children.find((c) => c.isBone);
        if (!child) continue;
        bone.getWorldPosition(clampA);
        child.getWorldPosition(clampB);
        const dir = clampB.sub(clampA).normalize();
        const axis = clampAxis.copy(dir).cross(DOWN);
        /* Degenerate only when the arm is dead vertical — straight up has no
           plane to come down through. Fall back to the coronal axis so it still
           lowers instead of silently doing nothing. */
        if (axis.lengthSq() < 1e-8) axis.copy(shoulderAxis).multiplyScalar(side === 'Left' ? -1 : 1);
        else axis.normalize();
        bone.rotateOnWorldAxis(axis, over * DEG);
        if (over > clampedBy) clampedBy = over;
      }
    }

    /* ⚠ AFTER the upper arm, for the parenting reason above. */
    if (elbow) {
      for (const side of ['Left', 'Right']) {
        const bone = B[`${side}ForeArm`];
        if (!bone) continue;
        const rad = elbow * DEG;
        bone.rotateOnWorldAxis(shoulderAxis, side === 'Left' ? -rad : rad);
      }
    }

    // What we leave on each bone, so next frame can tell OUR output apart from a
    // fresh clip pose. See beginCorrection.
    for (const bone of correctedBones) corrections.get(bone)?.out.copy(bone.quaternion);

    return reported ? { ...reported, applied: out * reported.openness, clampedBy } : null;
  };

  return { usable, apply, bones: B };
}

/** True when a stored pose actually asks for any of this — no numbers, no work. */
export function hasPoseFix(pose) {
  if (!pose) return false;
  return ['shoulderDrop', 'armOut', 'elbowIn', 'headTilt', 'armLiftMax']
    .some((k) => Number(pose[k]) || 0);
}

export default createPoseFix;
